import { ChangeDetectorRef, Component, OnInit, ViewChild } from '@angular/core';
import { DealService } from '@services/deal.service';
import {
    DealDocument,
    DealFormAnswersLittleI,
    DealModel,
    DealProcessByPipeline,
    DealProcessModel,
    DocumentCategory,
    StepHistory,
    TriggerModel,
} from '@models/deal.model';
import { ActivatedRoute } from '@angular/router';
import { FlexiformHelperService } from 'app/providers/helpers/flexiform.helper';
import { TogglerModel } from '@models/toggler.model';
import { CredentialsApiService } from '@services/api-calls/credentials-api.service';
import { ProgressBarComponent } from 'app/components/shared/progress-bar/progress-bar.component';
import { TabItemModel, TabModel } from '@models/tab.model';
import { NoteModel } from '@models/note.model';
import { NoteService } from '@services/note.service';
import { SnackbarService } from '@services/snackbar.service';
import { Snackbar } from '@models/snackbar.model';
import {
    DropdownItem,
    DropdownOptionInterface,
} from '@models/dropdown.interface';
import { TranslateService } from '@ngx-translate/core';
import { HelpersService } from '@providers/helpers/helpers';
import { DocumentService } from '@services/documents.service';
import { MultiuploadFilesComponent } from '@shared/multiupload-files/multiupload-files.component';
import { DocumentModel, MultiuploadDocuments } from '@models/multiupload.model';
import { ServerDocsService } from '@services/server-docs.service';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ModalModel } from '@models/modal.model';
import { CRUDService } from '@services/api-calls/crud.service';
import { DealflowStepI, PipelineI } from '@models/funnel.model';

@Component({
    selector: 'app-deal-page',
    templateUrl: './deal-page.component.html',
    styleUrls: ['./deal-page.component.scss'],
})
export class DealPageComponent implements OnInit {
    public dealInfo: DealModel;
    public dealId: string;
    public dealResponses: any;
    public modal: TogglerModel = new TogglerModel();
    public meeting: TogglerModel = new TogglerModel();
    public triggers: TriggerModel[];
    public processes: DealProcessModel[] = [];
    public processesByPipeline: DealProcessByPipeline[] = [];

    public fakeAvatar: string = 'assets/images/fake-avatar.svg';
    public notifications: boolean;
    public snackbar: Snackbar;
    public stringsTransalated: any;
    public tabs: TabModel;
    public subscription: any;
    public notes: NoteModel[] = [];
    public notesBackup: NoteModel[];
    public notesFilterItems: DropdownItem[] = [];
    public documentCategoriesBackup: DocumentCategory[] = [];
    public documentCategories: DocumentCategory[] = [];
    public documentCategoryNames: DropdownOptionInterface[] = [];
    public triggerCategoryName: string;
    public documentStrings: any;
    public documentsFilterItems: DropdownItem[] = [];
    public multiuploadToggler: TogglerModel = new TogglerModel(false);
    public noteForm: FormGroup;
    public addNotePopup: ModalModel = new ModalModel(false, '', '');
    public submitted: boolean = false;
    public publicNote: boolean = true; // BY DEFAULT ENTREPRENEURS NOTES ARE ALWAYS PUBLIC
    public companiesSelect: DropdownOptionInterface[] = [];
    public currentPipelineKey: string;

    @ViewChild(MultiuploadFilesComponent, { static: false })
    multiuploadComponent: MultiuploadFilesComponent;
    @ViewChild(ProgressBarComponent, { static: false })
    pbComponent: ProgressBarComponent;

    // ENTREPRENEUR AVAILABLE ACTIONS
    public CAN_CREATE_NOTES: boolean = true;
    public CAN_CREATE_NOTES_AS_PRIVATE: boolean = false;

    constructor(
        private crudService: CRUDService,
        private dealService: DealService,
        private route: ActivatedRoute,
        private flexiFormService: FlexiformHelperService,
        private credentialsApiService: CredentialsApiService,
        private cdRef: ChangeDetectorRef,
        private translate: TranslateService,
        private noteService: NoteService,
        private formBuilder: FormBuilder,
        private snackbarService: SnackbarService,
        private documentService: DocumentService,
        private helperService: HelpersService,
        private docService: ServerDocsService
    ) {
        this.subscription = this.credentialsApiService.appUser.subscribe(
            (response) => {
                if (this.dealInfo && response) {
                    this.dealInfo.step_history.deal.user_data.avatar =
                        response['avatar'];
                    this.checkAvatar();
                }
            }
        );
    }

    /**
     * If user avatar is null give it a default one
     */
    checkAvatar(): void {
        if (this.dealInfo.step_history.deal.coordinator_data) {
            this.dealInfo.step_history.deal.coordinator_data.avatar =
                this.dealInfo.step_history.deal.coordinator_data?.avatar != null
                    ? this.dealInfo.step_history.deal.coordinator_data.avatar
                    : this.fakeAvatar;
        }
    }

    ngOnInit(): void {
        this.snackbar = this.snackbarService.snackbarItem;
        this.noteForm = this.formBuilder.group({
            dealNote: ['', [Validators.required]],
        });
        this.dealId = this.route.snapshot.params.deal_id;
        this.credentialsApiService.getUserAccess().subscribe();
        this.getCompanies();
        this.getDocumentCategoryNames();
        this.translate
            .get('FUNNEL.DEAL_CARD_MENU.notificationsSuccessEnabled')
            .subscribe((translated: string) => {
                this.stringsTransalated = {
                    notificationsSuccessEnabled: translated,
                    notificationsSuccessDisabled: this.translate.instant(
                        'FUNNEL.DEAL_CARD_MENU.notificationsSuccessDisabled'
                    ),
                };
                this.tabs = new TabModel([
                    new TabItemModel(
                        0,
                        this.translate.instant('FUNNEL.dealPage.companyInfo'),
                        true
                    ),
                    new TabItemModel(
                        1,
                        this.translate.instant('FUNNEL.dealPage.notes'),
                        false
                    ),
                    new TabItemModel(
                        2,
                        this.translate.instant('FUNNEL.dealPage.documents')
                    ),
                ]);

                this.notesFilterItems = [
                    {
                        key: 'all',
                        icon: '',
                        value: this.translate.instant(
                            'FUNNEL.dealPage.notesFilters.all'
                        ),
                        children: [],
                    },
                    {
                        key: 'private',
                        icon: '',
                        value: this.translate.instant(
                            'FUNNEL.dealPage.notesFilters.private'
                        ),
                        children: [],
                    },
                    {
                        key: 'public',
                        icon: '',
                        value: this.translate.instant(
                            'FUNNEL.dealPage.notesFilters.public'
                        ),
                        children: [],
                    },
                ];

                this.documentStrings = {
                    allDocs: this.translate.instant(
                        'FUNNEL.dealPage.documentsFilter.all'
                    ),
                    uploadSuccess: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.uploadSuccess'
                    ),
                    uploadError: this.translate.instant(
                        'FUNNEL.dealPage.documentStrings.uploadError'
                    ),
                };

                this.addNotePopup.title = this.translate.instant(
                    'MODAL_NOTE.create.title'
                );
                this.addNotePopup.text = this.translate.instant(
                    'MODAL_NOTE.create.message'
                );

                this.triggerCategoryName = this.translate.instant(
                    'FUNNEL.dealPage.attachedDocument'
                );
            });
    }

    getCompanies(): void {
        const currentUser = this.credentialsApiService.currentAppUserValue;
        if (Array.isArray(currentUser['companies'])) {
            this.companiesSelect = [...currentUser['companies']].map(
                (company) => {
                    return { key: company.id, value: company.name };
                }
            );
        } else {
            this.companiesSelect = [
                {
                    key: currentUser['companies'].id,
                    value: currentUser['companies'].name,
                },
            ];
        }
        this.changeCompany(this.companiesSelect[0].key);
    }

    ngAfterViewInit() {
        this.loadDeal();
        this.cdRef.detectChanges();
    }

    getDocumentCategoryNames() {
        this.documentService.getDocumentCategories().subscribe(
            (response) => {
                this.documentCategoryNames = response[0].value
                    .split(',')
                    .map((item) => {
                        return {
                            key: this.helperService.slugify(item),
                            value: item,
                        };
                    });
            },
            (error) => {
                console.log(error);
            }
        );
    }

    // validUrl(str: string) {
    //     var pattern = new RegExp(
    //         '^(https?:\\/\\/)?' + // protocol
    //             '((([a-z\\d]([a-z\\d-]*[a-z\\d])*)\\.)+[a-z]{2,}|' + // domain name
    //             '((\\d{1,3}\\.){3}\\d{1,3}))' + // OR ip (v4) address
    //             '(\\:\\d+)?(\\/[-a-z\\d%_.~+]*)*' + // port and path
    //             '(\\?[;&a-z\\d%_.~+=-]*)?' + // query string
    //             '(\\#[-a-z\\d_]*)?$',
    //         'i'
    //     ); // fragment locator
    //     return !!pattern.test(str);
    // }

    changeCompany(companyId: string, reload: boolean = false): void {
        this.dealId = companyId;
        if (reload) {
            this.loadDeal();
        }
    }

    checkAnswerToFindUrls(): void {
        this.dealResponses.map((section) => {
            section.questions.map((response) => {
                const { answer } = response;
                if (this.validUrl(answer)) {
                    response.isUrl = true;
                    response.url = this.urlHasHTTP(answer)
                        ? answer
                        : `//${answer}`;
                } else {
                    response.isUrl = false;
                }
            });
        });
    }

    urlHasHTTP(url): boolean {
        return /(http(s)?):\/\//gi.test(url);
    }

    validUrl(str: string): boolean {
        return /[(http(s)?):\/\/(www\.)?a-zA-Z0-9@:%._\+~#=]{2,256}\.[a-z]{2,6}\b([-a-zA-Z0-9@:%_\+.~#?&//=]*)/gi.test(
            str
        );
    }

    loadDeal(): void {
        this.pbComponent.middle();
        const params = {
            //asset: ':(', // TODO: change to real data
        };
        //this.dealService.getDeal(this.dealId).subscribe(
        this.crudService
            .run('read', 'deal-info', 'common', this.dealId, params)
            .subscribe(
                (response: DealModel) => {
                    this.dealId = response.step_history.deal.id;
                    this.dealInfo = response;
                    this.dealResponses = this.dealInfo.form_documentation
                        ? this.flexiFormService.getQuestionResponseBySection(
                              this.dealInfo.form_documentation,
                              this.dealInfo.form_documentation_extended
                          )
                        : null;
                    this.parseQuestions();
                    this.checkAnswerToFindUrls();
                    this.getProcesses();
                    this.getTriggers();
                    this.getNotes();
                    this.getDocuments();
                    this.checkAvatar();
                    this.notifications =
                        this.dealInfo.step_history.deal.notifications_enabled;
                    const coordinator =
                        this.dealInfo.step_history.deal.coordinator_data;
                    if (coordinator) {
                        const coordinatorAvatar =
                            this.dealInfo.step_history.deal.coordinator_data
                                .avatar;
                        if (coordinatorAvatar) {
                            this.setCoordinatorAvatar(coordinatorAvatar);
                        } else {
                            this.dealInfo.step_history.deal.coordinator_data.avatar =
                                'assets/images/fake-avatar.svg';
                        }
                    }
                    this.pbComponent.end();
                },
                (error) => {
                    console.log(error);
                    this.pbComponent.error();
                }
            );
    }

    setCoordinatorAvatar(avatarID: string): void {
        this.docService.getImageV2(avatarID).then(
            (image) => {
                this.dealInfo.step_history.deal.coordinator_data.avatar = image;
            },
            (error) => {
                console.log(error);
            }
        );
    }

    /**
     * Builds an array that contains Document categories and inside of each
     * category his documents.
     */
    getDocuments(): void {
        this.documentCategories = [];
        this.documentsFilterItems = [
            {
                key: 'all_docs',
                icon: '',
                value: this.documentStrings.allDocs,
                children: [],
            },
        ];
        this.dealInfo.documentation.map((doc: DealDocument) => {
            const categoryName =
                doc.category === null ? this.triggerCategoryName : doc.category;
            const indexCategory = this.documentCategories.findIndex(
                (dc: DocumentCategory) => dc.name === categoryName
            );
            const document = new DealDocument(
                doc.category,
                doc.document_file,
                doc.identifier,
                doc.internal,
                doc.my_document,
                doc.name,
                doc.required,
                doc.updated_at,
                doc.user
            );
            if (indexCategory >= 0) {
                this.documentCategories[indexCategory].documents.push(document);
            } else {
                const newFilter: DropdownItem = {
                    key: this.helperService.slugify(categoryName),
                    icon: '',
                    value: categoryName,
                    children: [],
                };
                this.documentsFilterItems.push(newFilter);
                this.documentCategories.push(
                    new DocumentCategory(categoryName, [document])
                );
            }
        });

        this.documentCategoriesBackup = this.documentCategories;
    }

    parseQuestions(): void {
        if (this.dealResponses) {
            this.dealResponses.map((section: DealFormAnswersLittleI) => {
                const { title, questions } = section;
                return {
                    title,
                    questions: this.flexiFormService.questionParser(questions, [
                        {
                            nameID: 'founded',
                            questionType: 'date',
                        },
                        {
                            nameID: 'last_deal_size',
                            questionType: 'number',
                        },
                        {
                            nameID: 'total_raised',
                            questionType: 'number',
                        },
                    ]),
                };
            });
        }
    }

    getNotes(): void {
        this.notes = this.dealInfo.notes.map((note: NoteModel) => {
            return new NoteModel(
                note.id,
                note.title,
                note.content,
                note.coordinator_name,
                note.is_coordinator,
                this.dealId,
                note.internal,
                note.step_history_name,
                note.updated_at
            );
        });
        this.notesBackup = this.notes;
    }

    checkIfIsCurrentPipeline(pipeline: PipelineI): boolean {
        return pipeline.key === this.currentPipelineKey;
    }

    getProcesses(): void {
        this.processes = [];
        this.processesByPipeline = this.dealInfo.process;
        this.processesByPipeline.forEach((pbp: DealProcessByPipeline) => {
            pbp.pipeline.current = this.checkIfIsCurrentPipeline(pbp.pipeline);
            pbp.processes = [];
            pbp.step_history.forEach((step_history: StepHistory) => {
                pbp.processes.push(
                    new DealProcessModel(
                        step_history.updated_at,
                        step_history.step_key,
                        step_history.step_name
                    )
                );
            });
            pbp.triggers_completed.forEach((trigger) => {
                if (trigger.completed) {
                    pbp.processes.push(
                        new DealProcessModel(
                            trigger.updated_at,
                            null,
                            null,
                            trigger.trigger_data
                        )
                    );
                }
            });
            pbp.processes = this.sortProcesses(pbp.processes);
        });

        this.processesByPipeline =
            this.sortProcessesByPipelineByCurrentPipeline();
    }

    sortProcesses(processes): DealProcessModel[] {
        return processes.sort((a: DealProcessModel, b: DealProcessModel) => {
            return (
                <number>new Date(b.update_at).getTime() -
                <number>new Date(a.update_at).getTime()
            );
        });
    }

    sortProcessesByPipelineByCurrentPipeline() {
        this.processesByPipeline.sort((a, b) => {
            if (a.pipeline.current && !b.pipeline.current) {
                return -1; // a viene antes que b
            } else if (!a.pipeline.current && b.pipeline.current) {
                return 1; // b viene antes que a
            } else {
                return 0; // no se cambia el orden
            }
        });

        return this.processesByPipeline;
    }

    getTriggers(): void {
        // this.triggers = [
        //     new TriggerModel('doc', new Date().toString(), {}),
        //     new TriggerModel('meeting', new Date().toString(), {}),
        //     new TriggerModel('form', new Date().toString(), {}),
        // ];

        this.triggers = this.dealInfo.trigger_history
            .map((trigger: any) => {
                const triggerData = trigger.trigger_data;
                if (!trigger.completed) {
                    return new TriggerModel(
                        trigger.id,
                        triggerData.type,
                        trigger.updated_at,
                        triggerData,
                        '',
                        '',
                        trigger.completed,
                        this.dealId,
                        this.dealInfo.step_history.deal.coordinator_data
                    );
                }
            })
            .filter((item: any) => item != undefined);

        // this.triggers.push(
        //     new TriggerModel(12, 'doc', new Date().toString(), {
        //         name: 'test',
        //         step_file: 'test.png',
        //         info_name: 'Documento de test',
        //         type: 'doc',
        //     })
        // );
    }

    onTriggerCompleted(trigger: any) {
        document.getElementById('processes').scrollTo(0, 0);
        this.processes.unshift(
            new DealProcessModel(trigger.date, null, null, trigger.data, true)
        );
        if (trigger.type === 'doc') {
            const auxDocument = {
                name: trigger.data.info_name,
                document_file: trigger.data.step_file,
            };
            this.dealInfo.documentation.push(auxDocument);
        }
        this.reloadDealOn(1500);
    }

    /**
     * Reload the entire deal in 'time' miliseconds
     * @param time number of miliseconds to wait
     */
    reloadDealOn(time: number): void {
        setTimeout(() => {
            this.loadDeal();
        }, time);
    }

    manageNoteFilter(selected: DropdownItem): void {
        this.notes = this.notesBackup;
        switch (selected.key) {
            case 'private':
                this.notes = this.notes.filter(
                    (note) => note.internal === true
                );
                break;
            case 'public':
                this.notes = this.notes.filter(
                    (note) => note.internal === false
                );
                break;
            default:
                break;
        }
    }

    manageDocumentFilter(selected: DropdownItem): void {
        if (typeof selected === 'boolean') return;

        this.documentCategories = this.documentCategoriesBackup;
        const selectedValue = selected.value;
        if (selected.key !== 'all_docs') {
            this.documentCategories = this.documentCategories.filter(
                (dc: DocumentCategory) => dc.name === selectedValue
            );
        }
    }

    showNewNotePopUp(): void {
        this.noteForm.controls.dealNoteTitle.setValue('');
        this.noteForm.controls.dealNote.setValue('');
        this.submitted = false;
        this.addNotePopup.isVisible = true;
    }

    checkTab(): void {
        this.multiuploadComponent.pbComponent.hide();
    }

    /**
     * Called by New Note Popup.
     * If, dealNote has content, add this note to current deal
     */
    createNewNote(): void {
        this.submitted = true;
        if (!this.noteForm.invalid) {
            const data = {
                deal: this.dealId,
                title: this.noteForm.value.dealNoteTitle,
                content: this.noteForm.value.dealNote,
                internal: !this.publicNote,
            };
            const params = {
                //asset: ':(', // TODO: set real data
            };
            this.crudService
                .run('create', 'note', 'common', null, params, data)
                .subscribe(
                    () => {
                        this.loadDeal();
                    },
                    (error) => {
                        const msg =
                            error.error.key === 'DEAL_NOT_EXISTS'
                                ? this.translate.instant(
                                      'API.ERROR.KEY.DEAL_NOT_EXISTS'
                                  )
                                : error.error.detail;
                        this.snackbar.show(msg, 'error', true, 3000);
                    }
                );
            this.addNotePopup.close();
        }
    }

    showDocumentsPopUp(): void {
        this.multiuploadToggler.open();
    }

    /**
     *
     * @param documentCluster
     */
    uploadDocuments(documentCluster: MultiuploadDocuments[]): void {
        const params = {
            //company: ':(', // TODO: set real data
        };
        this.multiuploadComponent.pbComponent.show();
        this.multiuploadComponent.pbComponent.start();

        const promisesArray = [];
        documentCluster.forEach((docCluster: MultiuploadDocuments) => {
            const formData: FormData = new FormData();
            formData.append('deal', this.dealId);
            formData.append('category', docCluster.categoryName);
            docCluster.documents.forEach((document: DocumentModel, index) => {
                formData.append(`file_${index}`, document.file);
                formData.append(
                    `internal_${index}`,
                    document.internal.toString()
                );
            });

            this.multiuploadComponent.pbComponent.middle();
            promisesArray.push(
                new Promise((resolve, reject) => {
                    this.crudService
                        .run(
                            'create',
                            'documentation',
                            'woct',
                            null,
                            params,
                            formData
                        )
                        .subscribe(
                            () => {
                                resolve(true);
                            },
                            (error) => {
                                // todo: podemos poner el porque ha fallado en lugar del false.
                                reject(error);
                            }
                        );
                })
            );
        });
        Promise.allSettled(promisesArray).then(
            (results) => {
                // if all results are true, then all documents were uploaded
                if (results.every((result) => result.status === 'fulfilled')) {
                    this.multiuploadComponent.pbComponent.success();
                    this.multiuploadComponent.pbComponent.end();
                    this.multiuploadToggler.close();
                    this.loadDeal();
                    this.snackbar.show(
                        this.documentStrings.uploadSuccess,
                        'success',
                        true,
                        3000
                    );
                } else {
                    // print errors
                    const callsWithErrors = results.filter(
                        (result) => result.status === 'rejected'
                    );
                    console.log(callsWithErrors);
                    this.multiuploadComponent.pbComponent.error();
                    this.snackbar.show(
                        this.documentStrings.uploadError,
                        'error',
                        true,
                        3000
                    );
                }
            },
            (error) => {
                console.log(error);
            }
        );
    }

    ngOnDestroy() {
        this.credentialsApiService.currentAppUserSubject.unsubscribe();
    }
}
